import { useMutation, useQueryClient } from "@tanstack/react-query";

import {
  getListStravaAppsQueryKey,
  listStravaApps,
  stravaAuth,
} from "@/api/orval";
import { useCurrentUser } from "@/api/users";

// Custom hook: opens the Strava OAuth popup and resolves once the connection
// lands. Not generated because of the popup polling. Plain list/disconnect use
// the generated hooks directly.

function openPopup(url: string) {
  const width = 600;
  const height = 700;
  const left = window.screen.width / 2 - width / 2;
  const top = window.screen.height / 2 - height / 2;

  return window.open(
    url,
    "Connect with Strava",
    `toolbar=no,location=no,status=no,menubar=no,scrollbars=yes,resizable=yes,width=${width},height=${height},top=${top},left=${left}`,
  );
}

export function useConnectStrava() {
  const queryClient = useQueryClient();
  const { data: currentUser } = useCurrentUser();

  return useMutation({
    mutationFn: async () => {
      if (!currentUser) {
        throw new Error("Please log in before connecting to Strava");
      }

      const { auth_url } = (await stravaAuth()) as { auth_url: string };

      return new Promise((resolve, reject) => {
        const popup = openPopup(auth_url);
        if (!popup) {
          throw new Error(
            "Popup was blocked. Please allow popups for this site.",
          );
        }

        let popupClosed = false;
        let checkCount = 0;

        // Poll for changes in the popup state
        const checkPopup = setInterval(async () => {
          checkCount++;

          // Check if popup was closed
          if (!popup || popup.closed) {
            if (!popupClosed) {
              popupClosed = true;
              clearInterval(checkPopup);

              // After popup closes, check if connection was successful
              try {
                const apps = await listStravaApps();
                if (apps && apps.length > 0) {
                  queryClient.invalidateQueries({
                    queryKey: getListStravaAppsQueryKey(),
                  });
                  resolve("Connected to Strava successfully");
                } else {
                  reject(new Error("Failed to connect to Strava"));
                }
              } catch (error: any) {
                reject(
                  new Error(error.message || "Failed to connect to Strava"),
                );
              }
            }
            return;
          }

          // Timeout after 5 minutes (600 checks at 500ms intervals)
          if (checkCount > 600) {
            clearInterval(checkPopup);
            popup.close();
            reject(new Error("Connection timed out"));
            return;
          }
        }, 500);
      });
    },
  });
}
